import { useMemo, useRef, useState } from "react";
import { Upload } from "lucide-react";
import {
  Caption,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VButton,
  VDataTable,
  VSelect,
} from "@/components/ui";
import type { Asset, AssetStatus, DeploymentStatus } from "@/v2/shared/model";
import { formatPeriod, TODAY } from "@/v2/shared/format";
import { assetTypeLabels } from "@/v2/shared/statusMeta";
import { AssetStatusBadge, DeploymentStatusBadge } from "@/v2/shared/statusBadges";
import { useAssets, useRequests } from "@/v2/data/queries";
import { useImportAssets, useSetAssetStatus } from "@/v2/data/mutations";

// Manuele toestand: enkel dit kan de beheerder zelf zetten. "Beschikbaar" en
// "In gebruik" worden berekend uit de inzetten en zijn dus geen keuze.
const CONDITION_OPTIONS: ReadonlyArray<{ value: AssetStatus; label: string }> = [
  { value: "AVAILABLE", label: "Operationeel" },
  { value: "OUT_OF_ORDER", label: "Buiten werking" },
  { value: "IN_MAINTENANCE", label: "In onderhoud" },
];

type AssetBooking = {
  start: string;
  period: string;
  dossier: string;
  status: DeploymentStatus;
};

// Weergavestatus: buiten werking / in onderhoud (manueel) primeert; anders
// wordt de status afgeleid — In gebruik bij een lopende inzet, anders
// Beschikbaar.
function displayStatus(asset: Asset, inUse: boolean): AssetStatus {
  if (asset.status === "OUT_OF_ORDER" || asset.status === "IN_MAINTENANCE") {
    return asset.status;
  }
  return inUse ? "IN_USE" : "AVAILABLE";
}

export function AssetListPage(): React.JSX.Element {
  const assets = useAssets();
  const requests = useRequests();
  const setStatus = useSetAssetStatus();
  const importAssets = useImportAssets();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState("");

  // CB-33: bezetting per asset afgeleid uit de inzetten (verwachte periode),
  // incl. korte onderbrekingen/herinzet als aparte entries. Tegelijk bepalen
  // we welke assets nú in gebruik zijn (geplaatst, of periode omvat vandaag).
  const { bookingsByAsset, inUseAssetIds } = useMemo(() => {
    const map = new Map<string, ReadonlyArray<AssetBooking>>();
    const inUse = new Set<string>();
    for (const request of requests.data ?? []) {
      for (const dossier of request.dossiers) {
        for (const dep of dossier.deployments) {
          if (dep.status === "CLOSED") continue;
          const entry: AssetBooking = {
            start: dep.expectedStart,
            period: formatPeriod(dep.expectedStart, dep.expectedEnd),
            dossier: request.number,
            status: dep.status,
          };
          map.set(dep.assetId, [...(map.get(dep.assetId) ?? []), entry]);
          if (dep.status === "DEPLOYED" || (dep.expectedStart <= TODAY && TODAY <= dep.expectedEnd)) {
            inUse.add(dep.assetId);
          }
        }
      }
    }
    // Chronologisch: de eerstvolgende (of lopende) inzet bovenaan, zodat
    // opeenvolgende planningen als een leesbare tijdlijn verschijnen.
    for (const [assetId, bookings] of map) {
      map.set(assetId, [...bookings].sort((a, b) => (a.start < b.start ? -1 : 1)));
    }
    return { bookingsByAsset: map, inUseAssetIds: inUse };
  }, [requests.data]);

  // CB-29: Excel-oplading (gemockt) — herhaald opladen werkt bijwerkend.
  function handleFileChosen(files: FileList | null): void {
    const file = files?.item(0);
    if (!file) return;
    importAssets.mutate(undefined, {
      onSuccess: (result) =>
        setImportResult(
          `“${file.name}” verwerkt: ${result.added} asset(s) toegevoegd, ${result.updated} bijgewerkt.`,
        ),
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  if (assets.isPending) return <LoadingState label="Assets laden…" />;
  if (assets.isError) return <ErrorMessage title="Kon de assets niet laden" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Assets"
        subtitle="Mobiele camera-vloot, planning en status."
        actions={
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              aria-hidden
              tabIndex={-1}
              onChange={(e) => handleFileChosen(e.target.files)}
            />
            <VButton
              variant="outline"
              disabled={importAssets.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="size-4" aria-hidden />
              Excel opladen
            </VButton>
          </>
        }
      />

      <div className="flex items-center gap-2.5 rounded-xl border border-dashed border-neutral-300 px-4 py-2.5">
        <Upload className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <Caption tone="muted">
          {importResult ||
            "Assets worden opgeladen via Excel: bestaande nummers worden bijgewerkt, nieuwe toegevoegd (gemockt in dit prototype)."}
        </Caption>
      </div>

      <VDataTable<Asset>
        rows={assets.data ?? []}
        getRowKey={(a) => a.id}
        // Meerregelige planningkolom: alles bovenaan uitlijnen zodat de eerste
        // planning op dezelfde lijn staat als nummer, type en status.
        rowAlign="top"
        columns={[
          {
            key: "number",
            header: "Nummer",
            cell: (a) => a.number,
            className: "whitespace-nowrap font-medium tabular-nums",
          },
          { key: "type", header: "Type", cell: (a) => assetTypeLabels[a.type] },
          {
            key: "status",
            header: "Status",
            cell: (a) => <AssetStatusBadge status={displayStatus(a, inUseAssetIds.has(a.id))} />,
          },
          {
            key: "booking",
            header: "Planning",
            cell: (a) => {
              const bookings = bookingsByAsset.get(a.id) ?? [];
              if (bookings.length === 0) {
                return <span className="text-muted-foreground">Geen planning</span>;
              }
              return (
                <span className="block space-y-1.5">
                  {bookings.map((b) => (
                    <span
                      key={`${a.id}-${b.dossier}-${b.period}`}
                      className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
                    >
                      <DeploymentStatusBadge status={b.status} />
                      <span className="whitespace-nowrap font-medium tabular-nums text-foreground">
                        {b.period}
                      </span>
                      <span className="whitespace-nowrap text-muted-foreground">{b.dossier}</span>
                    </span>
                  ))}
                </span>
              );
            },
          },
          {
            key: "action",
            header: "Toestand",
            cell: (a) => (
              <VSelect
                aria-label={`Toestand van ${a.number}`}
                className="w-40"
                // In gebruik/beschikbaar is berekend en dus geen keuze; enkel
                // buiten werking of in onderhoud kan manueel gezet worden.
                value={
                  a.status === "OUT_OF_ORDER" || a.status === "IN_MAINTENANCE"
                    ? a.status
                    : "AVAILABLE"
                }
                disabled={setStatus.isPending}
                onChange={(e) => {
                  const next = CONDITION_OPTIONS.find((o) => o.value === e.target.value);
                  if (next) setStatus.mutate({ assetId: a.id, status: next.value });
                }}
              >
                {CONDITION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </VSelect>
            ),
          },
        ]}
      />
    </div>
  );
}
